<template>
  <div>
    <div class="md-layout">
      <!-- Profile Image Section -->
      <div class="md-layout-item md-size-30 md-small-size-100">
        <div class="picture-container">
          <div class="picture">
            <img
                :src="profileImage"
                :alt="labourer.name || 'Labourer Image'"
                @error="handleImageError"
            />
            <div class="picture-overlay" v-if="canEdit">
              <md-button class="md-just-icon md-round md-primary" @click="triggerImageUpload">
                <md-icon>camera_alt</md-icon>
              </md-button>
              <input
                  type="file"
                  ref="imageInput"
                  @change="uploadImage"
                  accept="image/*"
                  style="display: none"
              />
            </div>
          </div>
          <h6 class="description">
            <span v-if="labourer.image">Click image to change</span>
            <span v-else>No image available</span>
          </h6>
        </div>

        <!-- Quick Stats Cards -->
        <div class="quick-stats">
          <div class="stat-card" @click="$emit('show-takeons')">
            <md-icon>assignment_ind</md-icon>
            <div class="stat-info">
              <div class="stat-value">{{ getTotalTakeonCount() }}</div>
              <div class="stat-label">Takeons</div>
            </div>
          </div>
          <div class="stat-card" @click="$emit('show-skills')">
            <md-icon>star</md-icon>
            <div class="stat-info">
              <div class="stat-value">{{ getSkillsCount() }}</div>
              <div class="stat-label">Skills</div>
            </div>
          </div>
          <div class="stat-card" @click="$emit('show-assignments')">
            <md-icon :class="{ 'text-danger': labourer.fingerprint_count === 0 }">fingerprint</md-icon>
            <div class="stat-info">
              <div class="stat-value">{{ labourer.fingerprint_count }}</div>
              <div class="stat-label">{{ labourer.biodata_type }}</div>
            </div>
          </div>
        </div>

        <!-- Featured Current Activity Workplace (Below Quick Stats) -->
        <div class="featured-workplace-section" v-if="getCurrentActivityWorkplace()">
          <div class="featured-workplace-card" :class="{ 'skill-matched': isCurrentWorkplaceSkillMatched() }">

            <div class="featured-header">
              <div class="featured-title">
                <md-icon>stars</md-icon>
                <span>Current Active Assignment</span>
              </div>
              <badge type="success" class="active-badge">
                <md-icon>fiber_manual_record</md-icon>
                <span>ACTIVE</span>

                <md-icon v-if="isCurrentWorkplaceSkillMatched()" class="skill-match-icon" title="Skills match">verified</md-icon>
                <md-icon v-else class="skill-mismatch-icon" title="Skills mismatch">warning</md-icon>
              </badge>

            </div>

            <div class="featured-content">
              <div class="workplace-main-info">
                <div class="workplace-name">
                  <span>{{ getCurrentWorkplaceName() }}</span>
                </div>
                <div class="workplace-activity">
                  <span>{{ getCurrentActivityName() }}</span>
                </div>
              </div>

              <div class="workplace-date-range">
                <div class="date-badge">
                  <md-icon>event</md-icon>
                  <span>Started: {{ formatDate(getCurrentWorkplaceStartDate()) }}</span>
                </div>
                <div class="date-badge" v-if="getCurrentWorkplaceEndDate()">
                  <md-icon>event_busy</md-icon>
                  <span>Ends: {{ formatDate(getCurrentWorkplaceEndDate()) }}</span>
                </div>
                <div class="date-badge" v-else>
                  <md-icon>infinity</md-icon>
                  <span>No end date (Ongoing)</span>
                </div>
              </div>

              <div class="skill-matching-detail" v-if="getCurrentWorkplaceRequiredSkills().length">
                <div class="skill-matching-header">
                  <span class="matching-label">Skill Requirements:</span>
                  <span class="matching-percentage" :class="getSkillMatchClass()">
              {{ getCurrentWorkplaceSkillMatchPercentage() }}% Match
            </span>
                </div>

                <div class="skill-matching-list">
                  <div
                      v-for="skill in getCurrentWorkplaceRequiredSkills()"
                      :key="skill.id"
                      class="skill-matching-item"
                      :class="{ 'matched': isSkillOwned(skill), 'missing': !isSkillOwned(skill) }"
                  >
                    <md-icon>{{ isSkillOwned(skill) ? 'check_circle' : 'cancel' }}</md-icon>
                    <span>{{ skill.name }}</span>
                  </div>
                </div>

                <div v-if="!isCurrentWorkplaceSkillMatched()" class="skill-warning">
                  <md-icon>info</md-icon>
                  <span>Labourer missing required skills for this activity</span>
                </div>
              </div>

<!--              <div class="workplace-actions">-->
<!--                <md-button class="md-primary md-dense" @click="viewCurrentWorkplaceDetails">-->
<!--                  <md-icon>visibility</md-icon> View Details-->
<!--                </md-button>-->
<!--                <md-button class="md-info md-dense" @click="viewCurrentActivityDetails">-->
<!--                  <md-icon>work</md-icon> View Activity-->
<!--                </md-button>-->
<!--              </div>-->
            </div>
          </div>
        </div>

        <!-- No Active Assignment Warning -->
        <div v-else class="no-active-workplace">
          <md-icon class="no-active-icon">warning</md-icon>
          <div class="no-active-content">
            <h6>No Active Assignment</h6>
            <p>This labourer does not have any active workplace assignment.</p>
            <md-button class="md-primary md-simple" @click="$emit('add-workplace')">
              <md-icon>add</md-icon> Assign Workplace
            </md-button>
          </div>
        </div>
      </div>

      <!-- Details Section -->
      <div class="md-layout-item md-size-70 mt-4 md-small-size-100">
          <md-table>
            <row-item-view
                label="Labourer Number:"
                :value="getLabourerNumber()"
                icon="badge"
            />
            <row-item-view
                label="Full Name:"
                :value="getFullName()"
                icon="person"
            />
            <row-item-view
                label="NIDA Number:"
                :value="getNidaNumber()"
                icon="credit_card"
            />
            <row-item-view
                label="Phone Number:"
                :value="getPhoneNumber()"
                icon="phone"
            />
            <row-item-view
                label="Email:"
                :value="getEmail()"
                icon="email"
            />
            <row-item-view
                label="Gender:"
                :value="getGender()"
                icon="wc"
            />
            <row-item-view
                label="Birth Date:"
                :value="formatDate(labourer.birth_date)"
                icon="cake"
            />
            <row-item-view
                label="Skilled:"
                :value="labourer.skilled ? 'Yes' : 'No'"
                icon="engineering"
            />
            <row-item-view
                label="Biotime Status:"
                :value="labourer.biotime_id ? 'Registered' : 'Unregistered'"
                icon="fingerprint"
                :value-class="labourer.biotime_id ? 'text-success' : 'text-warning'"
            />
          </md-table>

          <!-- Assignment Information -->
          <div class="details-header mt-4">
            <h5 class="details-title">
              <md-icon>assignment</md-icon>
              Assignment Information
            </h5>
          </div>

          <md-table>
            <row-item-view
                label="Port:"
                :value="getPortName()"
                icon="business"
            />
            <row-item-view
                label="Department:"
                :value="getDepartmentName()"
                icon="account_balance"
            />
            <row-item-view
                label="Vendor:"
                :value="getVendorName()"
                icon="store"
            />
            <row-item-view
                label="Total Takeons:"
                :value="getTotalTakeonCount()"
                icon="assignment"
            />
            <row-item-view
                label="Completed Takeons:"
                :value="getCompletedTakeonCount()"
                icon="check_circle"
            />
          </md-table>

          <!-- Skills Section -->
          <div class="details-header mt-4">
            <h5 class="details-title">
              <md-icon>psychology</md-icon>
              Labourer Skills
            </h5>
            <badge type="info" class="count-badge">{{ getSkillsCount() }} Skills</badge>
          </div>

          <div class="skills-container" v-if="labourer.skills && labourer.skills.length">
            <badge
                v-for="skill in labourer.skills"
                :key="skill.id"
                type="success"
                class="skill-badge-large">
              {{ skill.name }}
            </badge>
          </div>
          <div v-else class="empty-skills">
            <md-icon>psychology</md-icon>
            <p>No skills assigned</p>
          </div>
      </div>
    </div>
  </div>
</template>

<script>
import RowItemView from '../../../components/Inputs/RowItemView';
import Badge from '@/components/Badge.vue';
import dateFormatMixin from "@/mixins/date-format-mixin";

export default {
  name: 'LabourerCard',
  components: { RowItemView, Badge },
  mixins: [dateFormatMixin],
  props: {
    labourer: {
      type: Object,
      required: true,
      default: function() {
        return {};
      }
    },
    canEdit: {
      type: Boolean,
      default: true
    }
  },
  data: function() {
    return {
      defaultImage: '/img/default-avatar.png'
    };
  },
  computed: {
    profileImage: function() {
      if (this.labourer.image && this.labourer.image !== 'null') {
        if (this.labourer.image.startsWith('http')) {
          return this.labourer.image;
        }
        return process.env.VUE_APP_BASE_URL + this.labourer.image;
      }
      return this.defaultImage;
    },

    labourerSkillIds: function() {
      if (!this.labourer.skills || !this.labourer.skills.length) {
        return [];
      }
      return this.labourer.skills.map(function(s) {
        return s.id;
      });
    }
  },
  methods: {
    // ============ SAFE GETTERS ============
    getLabourerNumber: function() {
      return this.labourer.labourer_number || 'N/A';
    },

    getNidaNumber: function() {
      return this.labourer.nida_number || 'N/A';
    },

    getPhoneNumber: function() {
      return this.labourer.phone_number || 'N/A';
    },

    getEmail: function() {
      return this.labourer.email || 'N/A';
    },

    getGender: function() {
      return this.labourer.gender || 'N/A';
    },

    getSkillsCount: function() {
      if (!this.labourer.skills) return 0;
      return this.labourer.skills.length;
    },

    getCurrentActivityWorkplace: function() {
      if (!this.labourer.activityLabourerWorkplace) {
        return null;
      }

      var workplace = this.labourer.activityLabourerWorkplace;
      var today = new Date();
      today.setHours(0, 0, 0, 0);

// Check if it's currently active
      if (!workplace.start_date) return null;

      var startDate = new Date(this.toLocal(workplace.start_date)) //new Date(workplace.start_date);
      startDate.setHours(0, 0, 0, 0);
      if (startDate > today) return null;

      if (workplace.end_date) {
        var endDate = new Date(this.toLocal(workplace.end_date));
        endDate.setHours(23, 59, 59, 999);
        if (endDate < today) {
          return null;
        }
      }
      return workplace;
    },

    getCurrentWorkplaceName: function() {
      var workplace = this.getCurrentActivityWorkplace();
      if (!workplace || !workplace.workplace) return 'N/A';
      return workplace.workplace.name;
    },

    getCurrentActivityName: function() {
      var workplace = this.getCurrentActivityWorkplace();
      if (!workplace || !workplace.activity) return 'N/A';
      return workplace.activity.name;
    },

    getCurrentWorkplaceStartDate: function() {
      var workplace = this.getCurrentActivityWorkplace();
      if (!workplace) return null;
      return workplace.start_date;
    },

    getCurrentWorkplaceEndDate: function() {
      var workplace = this.getCurrentActivityWorkplace();
      if (!workplace) return null;
      return workplace.end_date;
    },

    getCurrentWorkplaceRequiredSkills: function() {
      var workplace = this.getCurrentActivityWorkplace();
      if (workplace && workplace.activity && workplace.activity.skills) {
        return workplace.activity.skills;
      }
      return [];
    },

    isCurrentWorkplaceSkillMatched: function() {
      var requiredSkills = this.getCurrentWorkplaceRequiredSkills();
      if (!requiredSkills.length) return true;

      var self = this;
      return requiredSkills.every(function(skill) {
        return self.isSkillOwned(skill);
      });
    },

    getCurrentWorkplaceSkillMatchPercentage: function() {
      var requiredSkills = this.getCurrentWorkplaceRequiredSkills();
      if (!requiredSkills.length) return 100;

      var self = this;
      var matchedCount = requiredSkills.filter(function(skill) {
        return self.isSkillOwned(skill);
      }).length;

      return Math.round((matchedCount / requiredSkills.length) * 100);
    },

    getSkillMatchClass: function() {
      var percentage = this.getCurrentWorkplaceSkillMatchPercentage();
      if (percentage === 100) return 'match-excellent';
      if (percentage >= 70) return 'match-good';
      if (percentage >= 50) return 'match-fair';
      return 'match-poor';
    },

    // ============ SKILL METHODS ============
    isSkillOwned: function(skill) {
      if (!skill || !skill.id) return false;
      return this.labourerSkillIds.indexOf(skill.id) !== -1;
    },

    // ============ HELPER METHODS ============
    getFullName: function() {
      var parts = [];
      if (this.labourer.first_name) parts.push(this.labourer.first_name);
      if (this.labourer.middle_name) parts.push(this.labourer.middle_name);
      if (this.labourer.last_name) parts.push(this.labourer.last_name);
      return parts.length ? parts.join(' ') : 'N/A';
    },

    getPortName: function() {
      if (this.labourer.port) {
        return this.labourer.port.name;
      }
      if (this.labourer.port_name) {
        return this.labourer.port_name;
      }
      return 'Not Found';
    },

    getDepartmentName: function() {
      if (this.labourer.department) {
        return this.labourer.department.name;
      }
      if (this.labourer.department_name) {
        return this.labourer.department_name;
      }
      return 'Not Found';
    },

    getVendorName: function() {
      if (this.labourer.vendor) {
        if (Array.isArray(this.labourer.vendor) && this.labourer.vendor.length) {
          if (this.labourer.vendor[0] && this.labourer.vendor[0].name) {
            return this.labourer.vendor[0].name;
          }
        }
        if (this.labourer.vendor.name) {
          return this.labourer.vendor.name;
        }
      }
      return 'Not Found';
    },

    getTotalTakeonCount: function() {
      if (!this.labourer.labourerSchedules) return 0;
      return this.labourer.labourerSchedules.length;
    },

    getCompletedTakeonCount: function() {
      if (!this.labourer.labourerSchedules) return 0;
      var self = this;
      return this.labourer.labourerSchedules.filter(function(s) {
        return s.status === 'completed' || s.status === 'Check Out';
      }).length;
    },

    formatDate: function(date) {
      if (!date) return 'N/A';
      try {
        return new Date(date).toLocaleDateString();
      } catch (e) {
        return date;
      }
    },

    handleImageError: function(e) {
      e.target.src = this.defaultImage;
    },

    triggerImageUpload: function() {
      this.$refs.imageInput.click();
    },

    uploadImage: async function(event) {
      var file = event.target.files[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        this.$store.dispatch('alerts/error', 'Please select an image file');
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        this.$store.dispatch('alerts/error', 'Image size should be less than 2MB');
        return;
      }

      try {
        await this.$store.dispatch('labourers/upload', this.labourer,file);

        this.$store.dispatch('alerts/success', 'Profile image updated successfully');
        this.$emit('image-updated');

        await this.$store.dispatch('labourers/get', this.labourer.id);
      } catch (error) {
        console.error('Error uploading image:', error);
        this.$store.dispatch('alerts/error', 'Failed to upload image');
      }

      this.$refs.imageInput.value = '';
    },

    viewCurrentWorkplaceDetails: function() {
      var workplace = this.getCurrentActivityWorkplace();
      if (workplace) {
        this.$emit('view-workplace', workplace);
      }
    },

    viewCurrentActivityDetails: function() {
      var workplace = this.getCurrentActivityWorkplace();
      if (workplace && workplace.activity) {
        this.$emit('view-activity', workplace.activity);
      }
    }
  }
};
</script>

<style scoped>
.picture-container {
  text-align: center;
  margin-bottom: 20px;
}

.picture {
  width: 200px;
  height: 200px;
  margin: 0 auto;
  position: relative;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #9c27b0;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.picture img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.picture-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  padding: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.picture:hover .picture-overlay {
  opacity: 1;
}

.description {
  margin-top: 12px;
  color: #000;
  font-size: 12px;
}

.quick-stats {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 12px 16px;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.stat-card md-icon {
  font-size: 28px;
  color: #9c27b0;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.stat-label {
  font-size: 11px;
  color: #000;
}

/* Featured Current Workplace Styles */
.featured-workplace-section {
  margin-bottom: 20px;
}

.featured-workplace-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 4px solid #9c27b0;
  transition: all 0.3s ease;
}

.featured-workplace-card.skill-matched {
  border-left-color: #4caf50;
}

.featured-workplace-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.featured-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
}

.featured-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #ff9800;
}

.featured-title .md-icon {
  font-size: 18px !important;
  width: 18px !important;
  height: 18px !important;
  min-width: 18px !important;
}

.active-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  padding: 4px 8px;
}

.active-badge .md-icon {
  font-size: 12px !important;
  width: 12px !important;
  height: 12px !important;
  min-width: 12px !important;
  margin: 0;
}

.workplace-main-info {
  margin-bottom: 16px;
}

.workplace-name {
  font-size: 18px;
  font-weight: 600;
  color: #333333;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.skill-match-icon {
  color: #4caf50 !important;
}

.skill-mismatch-icon {
  color: #f44336 !important;
}

.workplace-activity {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #000000;
  font-size: 13px;
}

.workplace-activity .md-icon {
  font-size: 16px !important;
  width: 16px !important;
  height: 16px !important;
  min-width: 16px !important;
}

.workplace-date-range {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding: 8px 0;
}

.date-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #000000;
  font-size: 12px;
}

.date-badge .md-icon {
  font-size: 14px !important;
  width: 14px !important;
  height: 14px !important;
  min-width: 14px !important;
  color: #9c27b0;
}

/* Skill Requirements Container */
.skill-matching-detail {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.skill-matching-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 12px;
}

.matching-label {
  color: #000000;
  font-weight: 500;
}

.matching-percentage {
  font-weight: bold;
}

.match-excellent { color: #4caf50; }
.match-good { color: #8bc34a; }
.match-fair { color: #ff9800; }
.match-poor { color: #f44336; }

.skill-matching-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.skill-matching-item {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #ffffff;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  border: 1px solid #e0e0e0;
}

.skill-matching-item.matched {
  background: #e8f5e9;
  border-color: #4caf50;
  color: #2e7d32;
}

.skill-matching-item.missing {
  background: #ffebee;
  border-color: #f44336;
  color: #c62828;
}

.skill-matching-item .md-icon {
  font-size: 14px !important;
  width: 14px !important;
  height: 14px !important;
  min-width: 14px !important;
}

.skill-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 6px 12px;
  background: #fff3e0;
  border-radius: 6px;
  font-size: 11px;
  color: #e65100;
}

.skill-warning .md-icon {
  font-size: 14px !important;
  width: 14px !important;
  height: 14px !important;
  min-width: 14px !important;
  color: #e65100 !important;
}

/* Action Buttons Positioning */
.workplace-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.workplace-actions .md-button {
  margin: 0;
}

/* Fallback Warning: No Active Assignment */
.no-active-workplace {
  background: #fff8e1;
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  margin-bottom: 20px;
  border: 1px solid #ffe082;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.no-active-icon {
  font-size: 48px !important;
  width: 48px !important;
  height: 48px !important;
  min-width: 48px !important;
  color: #ff9800 !important;
  margin-bottom: 12px;
}

.no-active-workplace h6 {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333333;
}

.no-active-workplace p {
  margin: 0 0 16px 0;
  color: #000000;
}

/* Details Sections */
.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.details-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #9c27b0;
}

.details-title md-icon {
  font-size: 20px;
}

.count-badge {
  font-size: 12px;
  padding: 4px 12px;
}

/* Skills */
.skills-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.skill-badge-large {
  padding: 6px 12px;
  font-size: 12px;
}

.empty-skills {
  text-align: center;
  padding: 20px;
  color: #999;
}

.empty-skills md-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

/* Utility Classes */
.text-success {
  color: #4caf50;
  font-weight: 500;
}

.text-warning {
  color: #ff9800;
  font-weight: 500;
}

.mt-4 {
  margin-top: 16px;
}

.skill-match-icon {
  color: #4caf50;
  font-size: 20px;
}

.skill-mismatch-icon {
  color: #ff9800;
  font-size: 20px;
}

/* Responsive */
@media (max-width: 768px) {
  .picture {
    width: 150px;
    height: 150px;
  }

  .quick-stats {
    flex-direction: column;
  }

  .workplace-date-range {
    flex-direction: column;
    gap: 8px;
  }

  .workplace-actions {
    flex-direction: column;
  }

  .workplace-actions .md-button {
    width: 100%;
  }

  .skill-matching-list {
    flex-direction: column;
  }
}
</style>
